import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Mail, Phone, Calendar, TrendingUp, FileText } from "lucide-react";
import { Loader2 } from "lucide-react";
import { DealDocuments } from "@/components/deals/DealDocuments";

const AthleteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: athlete, isLoading } = useQuery({
    queryKey: ["athlete", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athletes" as any)
        .select("*")
        .eq("id", id)
        .single();
      
      if (error) throw error;
      return data as any;
    },
  });

  const { data: deals } = useQuery({
    queryKey: ["athlete-deals", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals" as any)
        .select("*")
        .eq("athlete_id", id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as any[];
    },
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!athlete) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Athlete not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard/athletes")}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Athletes
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold">{athlete.name}</h1>
              <p className="text-lg text-muted-foreground mt-1">
                {athlete.sport} {athlete.position && `• ${athlete.position}`}
              </p>
            </div>
            {athlete.jersey_number && (
              <Badge variant="outline" className="text-2xl px-4 py-2">
                #{athlete.jersey_number}
              </Badge>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Deals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{athlete.active_deals_count}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Deal Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-green-600" />
                {formatCurrency(athlete.total_deals_value)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Academic Year
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold capitalize">{athlete.year || "N/A"}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant={athlete.eligibility_status === "eligible" ? "default" : "secondary"}>
                {athlete.eligibility_status}
              </Badge>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{athlete.email}</span>
            </div>
            {athlete.phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{athlete.phone}</span>
              </div>
            )}
            {athlete.date_of_birth && (
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>
                  {new Date(athlete.date_of_birth).toLocaleDateString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="deals" className="w-full">
          <TabsList>
            <TabsTrigger value="deals">Deals</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="deals">
            <Card>
              <CardHeader>
                <CardTitle>Recent Deals</CardTitle>
              </CardHeader>
              <CardContent>
                {deals && deals.length > 0 ? (
                  <div className="space-y-3">
                    {deals.map((deal) => (
                      <div
                        key={deal.id}
                        className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-accent/50"
                        onClick={() => navigate(`/dashboard/deals/${deal.id}`)}
                      >
                        <div>
                          <p className="font-medium">{deal.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {deal.brand_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatCurrency(deal.amount)}</p>
                          <Badge variant="outline">{deal.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">
                    No deals yet
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  All Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deals && deals.length > 0 ? (
                  <div className="space-y-8">
                    {deals.map((deal) => (
                      <div key={deal.id}>
                        <h3 className="font-semibold mb-4">{deal.title}</h3>
                        <DealDocuments dealId={deal.id} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">
                    No deals yet
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AthleteDetail;